import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { useUserContext } from "./authProvider";
import { NavigationBadge } from "../..";
import { ReportsInstance, TemplatesInstance } from "../../api/reportsApi";
import { UsersInstance } from "../../api/userApi";
import { StudyInstance } from "../../api/studyApi";
import { cn } from "../../lib/utils";
import { useDarkMode } from "../../hooks/useDarkMode";
import { useAccess } from "./accessContext";

const NAV_LINKS = [
  { label: "Badania",            href: "/" },
  { label: "Kalendarz",          href: "/calendar" },
  { label: "Planowane wizyty",   href: "/planned-visits" },
  { label: "Issues",             href: "/problems" },
  { label: "Do weryfikacji",     href: "/reports" },
  { label: "Statystyki",         href: "/statistics" },
  { label: "Panel administratora", href: "/admin", adminOnly: true },
];

const Navbar = ({ items }: { items: NavigationBadge[] }) => {
  const { userName } = useUserContext();
  const { instance } = useMsal();
  const { pathname } = useLocation();
  const { isDark, toggle } = useDarkMode();
  const access = useAccess();
  const links = NAV_LINKS.filter((l) => !l.adminOnly || access?.isAdmin);

  const clients = [UsersInstance, TemplatesInstance, ReportsInstance, StudyInstance];

  useEffect(() => {
    const token = localStorage.getItem("webApiAccessToken");
    if (token) {
      clients.forEach((x) => {
        x.defaults.headers.common["Authorization"] = "Bearer " + token;
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance]);

  return (
    <nav
      className="flex items-center h-14 px-10 gap-12 bg-[var(--sc-navbar)] shrink-0"
      style={{ zIndex: 100 }}
    >
      {/* Logo */}
      <a href="/" className="flex items-center shrink-0 no-underline">
        <img src="/logo_dark_mode.png" alt="Scientia" className="h-12 w-auto" />
      </a>

      {/* Nav links */}
      <div className="flex gap-8 flex-1">
        {links.map(({ label, href }) => {
          const isActive = href === "/"
            ? pathname === "/"
            : pathname.startsWith(href);
          return (
            <a
              key={href}
              href={href}
              className={cn(
                "text-[13px] no-underline transition-colors",
                isActive
                  ? "text-white/95"
                  : "text-white/50 hover:text-white/80"
              )}
            >
              {label}
            </a>
          );
        })}
      </div>

      {/* Right: user + logout */}
      <div className="flex items-center gap-5 shrink-0">
        {userName && (
          <span className="text-[12px] text-white/40 tracking-wide">
            {userName}
          </span>
        )}
        <button
          onClick={toggle}
          aria-label={isDark ? "Tryb jasny" : "Tryb ciemny"}
          className="text-white/40 hover:text-white/70 transition-colors bg-transparent border-none cursor-pointer p-0 flex items-center"
        >
          {isDark ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="5" />
              <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
              <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            </svg>
          )}
        </button>
        <button
          onClick={() => instance.logout()}
          className="text-[12px] text-white/35 hover:text-white/60 transition-colors bg-transparent border-none cursor-pointer p-0"
        >
          Wyloguj się
        </button>
      </div>
    </nav>
  );
};

export { Navbar };

